import {
  ChangeDetectionStrategy,
  Component,
  output,
  signal,
  input,
  effect,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  SelectorChipComponent,
  SelectorElement,
} from '../selector-chip/selector-chip.component';
import { SelectorDatepickerComponent } from '../selector-datepicker/selector-datepicker.component';
import { ChipsContainerComponent } from '../../../../shared/components/mapa/components/chips-container/chips-container.component';
import { TooltipDirective } from '../../../../shared/directives/tooltip.directive';
import { SELECT_INDICE_VEGETACION } from '../../enums/indices.enum';

@Component({
  selector: 'app-tele-menu-comparacion',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    SelectorChipComponent,
    SelectorDatepickerComponent,
    ChipsContainerComponent,
    TooltipDirective,
  ],
  templateUrl: './menu-comparacion.component.html',
  styleUrl: './menu-comparacion.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuComparacionComponent implements AfterViewInit, OnDestroy {
  isExpanded = signal(true);

  clickCloseButton = output();
  changeLeftForm = output();
  changeRightForm = output();
  // Inputs para recibir los FormGroups
  leftFormGroup = input<FormGroup>();
  rightFormGroup = input<FormGroup>();
  // Mapa de tooltips para índices, provisto por el padre
  indiceTooltips = input<Record<string, string>>();

  // Mantener compatibilidad temporal
  changePrimaryForm = output();
  changeSecondaryForm = output();
  primaryFormGroup = input<FormGroup>();
  secondaryFormGroup = input<FormGroup>();

  private subscriptions: Subscription[] = [];
  private isUpdatingLeft = false;
  private isUpdatingRight = false;

  constructor() {
    // Effect para manejar cambios en los FormGroups de entrada
    effect(() => {
      this.setupFormSubscriptions();
    });
  }

  /**
   * Configurar las suscripciones a los cambios de formulario
   */
  private setupFormSubscriptions(): void {
    // Limpiar suscripciones existentes
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];

    // Suscribirse a cambios en el formulario izquierdo
    const leftForm = this.leftFormGroup();
    if (leftForm) {
      const leftSubscription = leftForm.valueChanges.subscribe(value => {
        if (!this.isUpdatingLeft) {
          console.log('📝 Cambio en formulario izquierdo:', value);
          this.changeLeftForm.emit(value);
          this.changePrimaryForm.emit(value); // Compatibilidad temporal
        }
      });
      this.subscriptions.push(leftSubscription);
    }

    // Suscribirse a cambios en el formulario derecho
    const rightForm = this.rightFormGroup();
    if (rightForm) {
      const rightSubscription = rightForm.valueChanges.subscribe(value => {
        if (!this.isUpdatingRight) {
          console.log('📝 Cambio en formulario derecho:', value);
          this.changeRightForm.emit(value);
          this.changeSecondaryForm.emit(value); // Compatibilidad temporal
        }
      });
      this.subscriptions.push(rightSubscription);
    }
  }

  ngAfterViewInit(): void {
    // Configurar suscripciones iniciales
    this.setupFormSubscriptions();
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions to prevent memory leaks and errors
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.subscriptions = [];
  }

  // Método para actualizar el formulario izquierdo desde el componente padre
  updateLeftForm(value: any): void {
    console.log('🔧 Actualizando formulario izquierdo con valores:', value);
    this.isUpdatingLeft = true;
    const leftForm = this.leftFormGroup();
    if (leftForm) {
      leftForm.patchValue(value, { emitEvent: false });
      leftForm.updateValueAndValidity();
      console.log('✅ Formulario izquierdo actualizado:', leftForm.value);
    }
    this.isUpdatingLeft = false;
  }

  // Método para actualizar el formulario derecho desde el componente padre
  updateRightForm(value: any): void {
    console.log('🔧 Actualizando formulario derecho con valores:', value);
    this.isUpdatingRight = true;
    const rightForm = this.rightFormGroup();
    if (rightForm) {
      rightForm.patchValue(value, { emitEvent: false });
      rightForm.updateValueAndValidity();
      console.log('✅ Formulario derecho actualizado:', rightForm.value);
    }
    this.isUpdatingRight = false;
  }

  // Métodos legacy para compatibilidad temporal
  updatePrimaryForm(value: any): void {
    this.updateLeftForm(value);
  }

  updateSecondaryForm(value: any): void {
    this.updateRightForm(value);
  }

  // Datos para los selectores (copiados del componente principal)
  escalas = [
    { value: 'general', viewValue: 'Escala general' },
    { value: 'especifica', viewValue: 'Escala específica' },
  ];

  get indices() {
    return SELECT_INDICE_VEGETACION;
  }

  onCloseButton(): void {
    this.clickCloseButton.emit();
  }

  toggleExpanded(): void {
    this.isExpanded.update(v => !v);
  }

  onChangePrimaryForm(event: any): void {
    this.changePrimaryForm.emit(event);
  }
}
